// React Query hooks for the WB yards admin surface (Phase C2).
//
// Pairs with /api/v1/wb/yards/* — list / detail / create / patch / delete.
// `enc_id` here is the encrypted scalar id (snake_case throughout the WB
// module). DTOs mirror server/src/modules/wb/yards/types.ts exactly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror WbYardDto on the server) --------------------

export interface WbYard {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  readonly address: string | null;
  readonly timezone: string;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface CreateYardInput {
  name: string;
  code: string;
  address?: string | null;
  timezone?: string;
  is_active?: boolean;
}

export type UpdateYardInput = Partial<CreateYardInput>;

// -- Queries ----------------------------------------------------------------

export function useWbYards() {
  return useQuery<{ yards: WbYard[] }>({
    queryKey: queryKeys.wb.yards(),
    queryFn: async () => {
      const res = await apiClient.get<{ yards: WbYard[] }>(endpoints.wb.yards);
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useWbYard(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<{ yard: WbYard }>({
    queryKey: queryKeys.wb.yard(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbYard called without enc_id');
      const res = await apiClient.get<{ yard: WbYard }>(endpoints.wb.yard(encId));
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateYard() {
  const qc = useQueryClient();
  return useMutation<{ yard: WbYard }, Error, CreateYardInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ yard: WbYard }, CreateYardInput>(
        endpoints.wb.yards,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.yards() });
    },
  });
}

export function useUpdateYard(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ yard: WbYard }, Error, UpdateYardInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<{ yard: WbYard }, UpdateYardInput>(
        endpoints.wb.yard(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.yards() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.yard(encId) });
    },
  });
}

export function useDeleteYard(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(endpoints.wb.yard(encId));
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.yards() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.yard(encId) });
    },
  });
}
